import React, { useState, useEffect } from 'react';
import { PlayIcon, HeartIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function MessagesPortal() {
  const { token } = useParams();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    loadMessages();
  }, [token]);

  const loadMessages = async () => {
    try {
      // Get user_id from token
      const response = await axios.get(`${API_URL}/portal/messages/${token}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const playMessage = async (messageId, audioUrl) => {
    setPlayingId(messageId);
    
    // Track play count
    try {
      await axios.post(`${API_URL}/messages/${messageId}/play`);
    } catch (error) {
      console.log('Failed to track play');
    }
  };

  const categories = [
    { id: 'all', name: 'All Messages', icon: '💌' },
    { id: 'birthday', name: 'Birthday', icon: '🎂' },
    { id: 'love', name: 'Love', icon: '❤️' },
    { id: 'encouragement', name: 'Encouragement', icon: '💪' },
    { id: 'proud', name: 'Proud of You', icon: '⭐' },
    { id: 'miss_you', name: 'Missing You', icon: '🤗' }
  ];

  const filteredMessages = selectedCategory === 'all' 
    ? messages 
    : messages.filter(m => m.occasion === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-pink-600 mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-pink-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="text-center">
            <HeartIcon className="h-12 w-12 text-pink-500 mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages from Your Loved One</h1>
            <p className="text-gray-600">Hear their voice whenever you need it</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-pink-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-pink-50 border border-pink-200'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Messages Grid */}
        {filteredMessages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No messages in this category yet</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                className="bg-white rounded-2xl shadow-lg border border-pink-100 p-6 hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {message.occasion_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {message.type === 'dynamic' ? 'AI Generated' : 'Personal Message'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">
                      {new Date(message.created_at).toLocaleDateString()}
                    </p>
                    {message.play_count > 0 && (
                      <p className="text-xs text-pink-600 mt-1">
                        Played {message.play_count} {message.play_count === 1 ? 'time' : 'times'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-100">
                  <p className="text-gray-800 italic leading-relaxed">
                    "{message.text}"
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <audio
                    controls
                    src={`${API_URL}${message.audio_url}`}
                    className="flex-1"
                    onPlay={() => playMessage(message.id, message.audio_url)}
                    style={{ height: '40px' }}
                  />
                  <button
                    onClick={() => {
                      const audio = new Audio(`${API_URL}${message.audio_url}`);
                      audio.play();
                      playMessage(message.id, message.audio_url);
                    }}
                    className="p-3 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    <PlayIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="inline-block p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-pink-200">
            <p className="text-gray-600 text-sm">
              These messages were created with love using AI voice cloning technology
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Listen to these messages whenever you need to hear their voice
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
